import { useMemo, useState } from "react";
import { CheckCircle2, RotateCcw, ThumbsDown, ThumbsUp, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface DealFeedbackWidgetProps {
  pendingLabel?: string;
  positiveLabel?: string;
  negativeLabel?: string;
  className?: string;
  value?: FeedbackState;
  onValueChange?: (value: FeedbackState, previous: FeedbackState) => void | Promise<void>;
}

type FeedbackState = "pending" | "up" | "down";

export function DealFeedbackWidget({
  pendingLabel,
  positiveLabel,
  negativeLabel,
  className,
  value,
  onValueChange,
}: DealFeedbackWidgetProps) {
  const [internalState, setInternalState] = useState<FeedbackState>("pending");
  const state = value ?? internalState;

  const setState = async (next: FeedbackState) => {
    const previous = state;
    if (value === undefined) {
      setInternalState(next);
    }
    if (onValueChange) {
      try {
        await onValueChange(next, previous);
      } catch {
        if (value === undefined) {
          setInternalState(previous);
        }
      }
    }
  };

  const labels = useMemo(
    () => ({
      pending: pendingLabel?.trim() || "You decide",
      up: positiveLabel?.trim() || "Worth it",
      down: negativeLabel?.trim() || "Not",
    }),
    [pendingLabel, positiveLabel, negativeLabel]
  );

  const chipClass =
    state === "up"
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : state === "down"
        ? "bg-red-100 text-red-800 border-red-300"
        : "bg-white text-slate-700 border-slate-200";

  const label = state === "pending" ? labels.pending : state === "up" ? labels.up : labels.down;

  return (
    <div className={cn("rounded-xl border border-slate-200 bg-white p-2 shadow-sm inline-flex items-center gap-2", className)}>
      <div className={cn("rounded-full border px-4 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5", chipClass)}>
        {state === "up" && <CheckCircle2 className="h-4 w-4" />}
        {state === "down" && <XCircle className="h-4 w-4" />}
        {label}
      </div>

      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          className={cn(
            "h-8 w-8 rounded-md border border-transparent inline-flex items-center justify-center",
            state === "up" ? "bg-slate-900 text-white" : "text-slate-800 hover:bg-slate-100"
          )}
          onClick={(e) => {
            e.stopPropagation();
            setState("up");
          }}
          aria-label="Mark thumbs up"
        >
          <ThumbsUp className="h-4 w-4" />
        </button>

        <button
          type="button"
          className={cn(
            "h-8 w-8 rounded-md border border-transparent inline-flex items-center justify-center",
            state === "down" ? "bg-red-600 text-white" : "text-slate-800 hover:bg-slate-100"
          )}
          onClick={(e) => {
            e.stopPropagation();
            setState("down");
          }}
          aria-label="Mark thumbs down"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>

        <button
          type="button"
          className="h-8 w-8 rounded-md border border-transparent inline-flex items-center justify-center text-slate-800 hover:bg-slate-100"
          onClick={(e) => {
            e.stopPropagation();
            setState("pending");
          }}
          aria-label="Reset feedback"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default DealFeedbackWidget;
